/**
 * TagPicker — multi-select combobox de tags pra adicionar/remover de um card
 * (FEAT-V2-031). Aceita workflow opcional pra filtrar catálogo.
 *
 * Comportamento:
 *   - Mostra tags atuais como `<TagBadge>` removíveis
 *   - "+ Adicionar tag" abre dropdown com catálogo filtrado por workflow
 *   - Tags já no card aparecem disabled no dropdown
 *   - Search por nome no dropdown
 *   - Admin com canQuickAdd=true ganha rodapé "+ Nova tag" que abre
 *     TagQuickAddDialog
 */

import { useMemo, useState, useRef } from 'react';

import { TagBadge } from './TagBadge';
import { TagQuickAddDialog } from './TagQuickAddDialog';
import { Input, Popover } from '@/components/ui';
import { useTagsCatalog, useCardTagActions, type Tag, type CardType } from '@/lib/tags-store';

interface TagPickerProps {
  cardType: CardType;
  cardId: string;
  /** Tags atualmente no card (já vem do useCardTags do caller). */
  currentTags: Tag[];
  /** Filtra catálogo a tags permitidas pra esse workflow. NULL = global. */
  workflowSlug?: string | null;
  /** Mostra botão "+ Nova tag" no rodapé do dropdown. */
  canQuickAdd?: boolean;
  /** Layout compacto pra usar dentro de modal/sidebar. */
  compact?: boolean;
}

export function TagPicker({
  cardType,
  cardId,
  currentTags,
  workflowSlug = null,
  canQuickAdd = false,
  compact = false,
}: TagPickerProps) {
  const { data: catalog = [] } = useTagsCatalog(workflowSlug);
  const { add, remove } = useCardTagActions(cardType, cardId);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [quickAddOpen, setQuickAddOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  // CENSO-FLUTUANTE (05/08): click-fora/Esc agora são do <Popover> canônico.

  const currentIds = useMemo(() => new Set(currentTags.map((t) => t.id)), [currentTags]);
  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return catalog;
    return catalog.filter((t) => t.nome.toLowerCase().includes(term));
  }, [catalog, search]);

  function handleAdd(tagId: string) {
    add.mutate(tagId);
    setSearch('');
  }

  function handleRemove(tagId: string) {
    remove.mutate(tagId);
  }

  return (
    <div data-testid="tag-picker" ref={containerRef} className="relative">
      <div className="flex flex-wrap items-center gap-1.5">
        {currentTags.map((t) => (
          <TagBadge
            key={t.id}
            tag={t}
            size={compact ? 'sm' : 'md'}
            removable
            onRemove={() => handleRemove(t.id)}
          />
        ))}
        <button
          type="button"
          data-testid="tag-picker-toggle"
          onClick={() => setOpen((o) => !o)}
          className={`inline-flex items-center gap-1 rounded-full border border-dashed border-line dark:border-line-dark text-ink-3 dark:text-ink-dark-3 hover:bg-paper-warm dark:hover:bg-paper-dark-warm transition-colors ${
            compact ? 'text-xs px-1.5 py-0.5' : 'text-xs px-2 py-1'
          }`}
        >
          + Tag
        </button>
      </div>

      {/* CENSO-FLUTUANTE (05/08): era absolute (clipado no ChamadoDealDetailDialog,
          Dialog lg) — migrado pro <Popover> canônico em portal. */}
      <Popover
        aberto={open}
        gatilhoRef={containerRef}
        onFechar={() => {
          setOpen(false);
          setSearch('');
        }}
      >
        <div
          data-testid="tag-picker-dropdown"
          role="listbox"
          className="w-72 max-w-[calc(100vw-2rem)] rounded-xl border border-line dark:border-line-dark bg-paper-surface dark:bg-paper-dark-surface shadow-xl"
        >
          <div className="p-2 border-b border-line dark:border-line-dark">
            <Input
              data-testid="tag-picker-search"
              placeholder="Buscar tag..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              autoFocus
            />
          </div>
          <div className="max-h-64 overflow-y-auto py-1">
            {filtered.length === 0 ? (
              <p className="text-xs text-ink-3 dark:text-ink-dark-3 px-3 py-2 italic">
                Nenhuma tag encontrada.
              </p>
            ) : (
              filtered.map((t) => {
                const already = currentIds.has(t.id);
                return (
                  <button
                    key={t.id}
                    type="button"
                    disabled={already}
                    onClick={() => handleAdd(t.id)}
                    data-testid={`tag-picker-option-${t.id}`}
                    className={`w-full text-left px-3 py-1.5 flex items-center gap-2 text-xs transition-colors ${
                      already
                        ? 'opacity-40 cursor-not-allowed'
                        : 'hover:bg-paper-warm dark:hover:bg-paper-dark-warm cursor-pointer'
                    }`}
                  >
                    <TagBadge tag={t} size="sm" />
                    {already && (
                      <span className="ml-auto text-ink-3 text-xs">✓ no card</span>
                    )}
                  </button>
                );
              })
            )}
          </div>
          {canQuickAdd && (
            <div className="border-t border-line dark:border-line-dark p-2">
              <button
                type="button"
                data-testid="tag-picker-quick-add"
                onClick={() => {
                  setQuickAddOpen(true);
                  setOpen(false);
                }}
                className="w-full text-xs text-red hover:text-red-deep px-2 py-1 text-left transition-colors"
              >
                + Criar nova tag…
              </button>
            </div>
          )}
        </div>
      </Popover>

      {quickAddOpen && (
        <TagQuickAddDialog
          workflowSlug={workflowSlug}
          onClose={() => setQuickAddOpen(false)}
          onCreated={(tag) => {
            setQuickAddOpen(false);
            // Catálogo invalida pela mutation — só adiciona ao card
            add.mutate(tag.id);
          }}
        />
      )}
    </div>
  );
}
